import { CARD_BACK_FACES } from './cardBackFaces';
import { desugarAutomationEntries } from '../game/engine/automation/schema';
import { sorceryImageUrl } from '../cardImageUrl';
import { SYNTHETIC_FOIL_SLUGS } from './syntheticFoils';

const SORCERY_CDN_BASE = 'https://d27a44hjr9gen3.cloudfront.net/cards';

export function buildSorceryCdnImageUrl(variantSlug) {
  return `${SORCERY_CDN_BASE}/${variantSlug}.png`;
}

const RARITY_CODE_MAP = {
  'Ordinary': 'O',
  'Exceptional': 'E',
  'Elite': 'L',
  'Unique': 'U',
};

const FOILING_CODE_MAP = {
  'Standard': 'S',
  'Foil': 'F',
};

function parseElements(elementsStr) {
  if (!elementsStr || elementsStr === 'None') return [];
  return elementsStr.split(',').map(e => e.trim()).filter(Boolean);
}

function parseSubTypes(subTypesStr) {
  if (!subTypesStr) return [];
  return subTypesStr.split(',').map(s => s.trim()).filter(Boolean);
}

function getSorceryCategory(type) {
  if (type === 'Site') return 'Site';
  if (type === 'Avatar') return 'Avatar';
  return 'Spell';
}

function statToString(value) {
  if (value === null || value === undefined) return '';
  return String(value);
}

// Printed status keywords surfaced directly on the card. Lowercased
// so they sit alongside the runtime's statuses / _auraStatuses sets,
// all comparable with .includes(). Used by scope filter checks
// (filter.status='spellcaster' etc.) and the v1.14 spellcaster-
// eligibility check.
const PRINTED_KEYWORD_NAMES = [
  'Airborne', 'Burrowing', 'Submerge', 'Stealth', 'Voidwalk',
  'Ward', 'Lethal', 'Charge', 'Immobile', 'Ranged', 'Spellcaster',
  // Lance is the entry signal for the auto-equipped Lance token
  // (grantLanceOnEntry). Its DISPLAY + state stay equipment-driven
  // (equippedCards via displayStatusKeys), so getEffectiveStatuses
  // deliberately drops 'lance' from the printed-status union. The badge
  // must vanish when the Lance breaks, not persist from the keyword.
  'Lance',
  // v1.90. Auto-recognize from printed keyword lines so cards like
  // Cave Trolls (Landbound) get their status without the player
  // toggling it manually on every cast. The radial menu already
  // surfaces these for manual override per [[feedback_ui_consistency]].
  // Movement +N is intentionally NOT here. Parameterised match
  // already handles it, and a binary status loses the +N value.
  'Landbound', 'Waterbound', 'Flooded',
];
const PRINTED_KEYWORD_SET = new Set(PRINTED_KEYWORD_NAMES.map((k) => k.toLowerCase()));

// Printed PHRASE abilities that behave like keywords: the whole rules line
// (lowercased, punctuation-stripped) maps to an internal keyword the engine
// reads via hasKeyword. Escyllion Cyclops (#40): "Doesn't strike back while
// defending" → resolveFight excludes it from the strike-back sum.
const PHRASE_KEYWORDS = new Map([
  ["doesn't strike back while defending", 'no_strike_back'],
  // Tawny / Recurring Specter. canDefend reads 'cant_defend' via
  // hasKeyword, so silence lifts the restriction with the rest of the
  // box text. Lumbering Giant prints the combined form; both halves
  // surface (a value may be an array of keywords).
  ["can't defend", 'cant_defend'],
  ["can't defend or intercept", ['cant_defend', 'cant_intercept']],
]);

/**
 * Pull intrinsic keywords off a card's rules text.
 *
 * Sorcery's convention: printed keywords occupy the LEADING lines of
 * the rules text. A keyword line is either a single keyword
 * ("Airborne") or a comma-separated list ("Stealth, Voidwalk"). Some
 * lines mix recognised keywords with parameterised movement modifiers
 * like "Movement +2." Phantom Steed prints `Movement +2, Voidwalk`
 * as one line. We treat parameterised "Keyword +N" / "Keyword N"
 * tokens as keyword-line tokens too: they don't end the scan AND, if
 * the bare keyword (after stripping the +N) is in our set, the
 * stripped form is added to the found set (so "Ranged 3" → "ranged").
 *
 * A line is scanned left-to-right and contributes only its LEADING
 * keyword tokens; the first non-keyword token ends collection for THAT
 * line. Crucially the scan does NOT stop there. It continues to later
 * lines, because the keyword band isn't always first. Sewer Rats prints
 * a prose rule ("May be cast under any site.") above its keyword band
 * ("Burrowing, Submerge"). A prose line therefore only contributes
 * nothing, it doesn't suppress a keyword band that follows it. Inline
 * keyword mentions in prose still don't leak: a prose line starts with a
 * non-keyword token, so its leading-token set is empty.
 *
 * Blank lines between keyword lines are skipped (some printings use a
 * blank separator between the keyword band and the first ability line).
 */
const PARAMETERISED_KEYWORD_RE = /^([A-Z][a-z]+)(?:\s\+?\d+)$/;

// Some printings prefix a keyword line with "UPDATED:" / "Updated:" to
// mark a rules-update reprint (e.g. "UPDATED: Airborne"). Strip the
// marker before tokenizing so the keyword behind it still surfaces.
const UPDATED_PREFIX_RE = /^(?:UPDATED|Updated):\s*/;

function stripUpdatedPrefix(line) {
  return line.replace(UPDATED_PREFIX_RE, '');
}

function classifyKeywordToken(token) {
  if (PRINTED_KEYWORD_SET.has(token.toLowerCase())) {
    return { kind: 'matched', value: token.toLowerCase() };
  }
  const phrase = PHRASE_KEYWORDS.get(token.toLowerCase().replace(/’/g, "'"));
  if (phrase) return { kind: 'matched', value: phrase };
  const paramMatch = token.match(PARAMETERISED_KEYWORD_RE);
  if (paramMatch) {
    // "Ranged 3" → matched as 'ranged'; "Movement +2" → keyword-line
    // token but no matching status to record.
    const bare = paramMatch[1].toLowerCase();
    if (PRINTED_KEYWORD_SET.has(bare)) return { kind: 'matched', value: bare };
    return { kind: 'parameterised' };
  }
  return null; // sentence / prose. Ends the keyword scan
}

// Extract the numeric bonus from a `Movement +N` token in the rules
// text. Returns the bonus (e.g. 2 for Phantom Steed's "Movement +2")
// or 0 when no such token is present. Only the FIRST occurrence is
// counted from the printed rules. Stacking from aura-granted
// "Movement +N" auras (Mad Dash etc.) happens at runtime via
// engine state, not via the static catalog.
//
// Note: same scan rules as extractPrintedKeywords. The line ends
// when prose appears, so prose mentions of "movement" inside
// reminder text (rare but possible) are ignored.
const MOVEMENT_BONUS_RE = /^Movement\s\+?(\d+)$/;
const RANGED_VALUE_RE   = /^Ranged\s(\d+)$/;

export function extractMovementBonus(rulesText) {
  if (!rulesText) return 0;
  const lines = rulesText.split(/\r?\n/);
  for (const line of lines) {
    const trimmed = stripUpdatedPrefix(line.trim());
    if (!trimmed) continue;
    const tokens = trimmed
      .split(',')
      .map((t) => t.trim().replace(/[.,;:!?]+$/, ''))
      .filter(Boolean);
    if (tokens.length === 0) continue;
    // Walk tokens left-to-right; the first prose token ends collection
    // for this line only, then we move to the next line (the keyword
    // band may sit below a prose rule. See extractPrintedKeywords).
    for (const token of tokens) {
      if (classifyKeywordToken(token) === null) break;
      const m = token.match(MOVEMENT_BONUS_RE);
      if (m) return parseInt(m[1], 10) || 0;
    }
  }
  return 0;
}

/**
 * Numeric N from "Ranged" / "Ranged N" lines (v1.56). Same
 * prose-detection guard as extractMovementBonus. Only scans
 * keyword-flavoured lines. Codex: bare "Ranged" shoots a projectile
 * that stops after 1 step; "Ranged X" replaces the 1 with X. So the
 * bare keyword resolves to 1, never 0. Returns 0 only for units
 * without the keyword.
 */
export function extractRangedValue(rulesText) {
  if (!rulesText) return 0;
  const lines = rulesText.split(/\r?\n/);
  for (const line of lines) {
    const trimmed = stripUpdatedPrefix(line.trim());
    if (!trimmed) continue;
    const tokens = trimmed
      .split(',')
      .map((t) => t.trim().replace(/[.,;:!?]+$/, ''))
      .filter(Boolean);
    if (tokens.length === 0) continue;
    for (const token of tokens) {
      if (classifyKeywordToken(token) === null) break;
      const m = token.match(RANGED_VALUE_RE);
      if (m) return parseInt(m[1], 10) || 0;
      if (token.toLowerCase() === 'ranged') return 1;
    }
  }
  return 0;
}

export function extractPrintedKeywords(rulesText) {
  if (!rulesText) return [];
  const found = new Set();
  // Lance is the one keyword where COUNT matters (codex: multiple Lances
  // each add +1 damage; Thankless Squire prints "Lance, Lance, Lance").
  // Every consumer reads keywords via includes()/Set-union, so duplicate
  // 'lance' entries are safe; the entry grant counts them.
  let lanceCount = 0;
  const lines = rulesText.split(/\r?\n/);

  for (const line of lines) {
    const trimmed = stripUpdatedPrefix(line.trim());
    if (!trimmed) continue;

    // Comma-separated tokens. Strip any trailing punctuation so a
    // stray period on the keyword line doesn't break detection.
    const tokens = trimmed
      .split(',')
      .map((t) => t.trim().replace(/[.,;:!?]+$/, ''))
      .filter(Boolean);
    if (tokens.length === 0) continue;

    // Walk tokens left-to-right, accepting leading keyword/parameterised
    // tokens until the first prose token ends this line. Then move to
    // the next line. A keyword band can sit below a prose rule (Sewer
    // Rats: "May be cast under any site." then "Burrowing, Submerge").
    // "Charge, May carry an ally." → keep 'charge', stop this line.
    // "Stealth, Voidwalk" → keep both.
    for (const token of tokens) {
      const c = classifyKeywordToken(token);
      if (c === null) break;
      if (c.kind === 'matched') {
        for (const v of [].concat(c.value)) {
          if (v === 'lance') lanceCount += 1;
          found.add(v);
        }
      }
    }
  }

  const out = [...found];
  for (let i = 1; i < lanceCount; i += 1) out.push('lance');
  return out;
}

// Card images are stored and served as WebP. The server converts PNGs
// from the upstream Sorcery CDN on the fly (see src/bun/runtime.js
// handleSorceryImage + downloadBatch). Clients always request .webp URLs.
//
// Foil slugs (`-f`, foil back `-f-r`) used to alias the standard art and
// only got their own art later. Card images are served `immutable`, so any
// client that cached a foil URL during the alias era keeps painting the old
// standard bytes forever. Append a cache-bust token to foil URLs to force a
// refetch. Standard slugs never changed their bytes, so they don't need it.
//
// The token is the app version (see cardsApi.js → ensureClientVersion):
// card data + images ship with a release, so the version changes exactly
// when the card blob a user downloads is updated, and foils auto-refetch
// without anyone bumping a constant. FOIL_IMAGE_CACHE_BUST is the fallback
// floor used when the version is unavailable (dev, or status fetch failed);
// it must stay != the historical `2` so the fallback still supersedes the
// stale alias-era cache. The server ignores unknown query params (it only
// reads `?v=`), so any token value is safe here.
const FOIL_IMAGE_CACHE_BUST = 3;
function buildSorceryImageUrl(variantSlug, imageBaseUrl, imageCacheBust = FOIL_IMAGE_CACHE_BUST) {
  const bust = /-f(-r)?$/.test(variantSlug) ? `?cb=${imageCacheBust}` : '';
  return `${sorceryImageUrl(variantSlug, imageBaseUrl)}${bust}`;
}

export function normalizeSorceryCard(raw, imageBaseUrl = '', automationMap = null, imageCacheBust = FOIL_IMAGE_CACHE_BUST) {
  const guardian = raw.guardian || {};
  const type = guardian.type || 'Minion';
  const elements = parseElements(raw.elements);
  const subTypes = parseSubTypes(raw.subTypes);
  const slug = raw.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  const printings = [];
  for (const set of raw.sets || []) {
    const setRarity = set.metadata?.rarity || guardian.rarity;
    for (const variant of set.variants || []) {
      if (variant.finish === 'Rainbow') continue;
      printings.push({
        unique_id: variant.slug,
        set_printing_unique_id: variant.slug,
        id: variant.slug,
        set_id: set.name,
        // Source product (Booster / Box_Topper / Dust / Preconstructed_Deck …).
        // Set-completion achievements use this to count only booster-obtainable
        // cards. Tokens and box-toppers must never gate a "collect every X" set.
        product: variant.product || '',
        edition: (variant.finish || 'S')[0].toUpperCase(),
        foiling: FOILING_CODE_MAP[variant.finish] || 'S',
        rarity: RARITY_CODE_MAP[setRarity] || '',
        expansion_slot: false,
        artists: variant.artist ? [variant.artist] : [],
        art_variations: [],
        flavor_text: variant.flavorText || '',
        // Sorcery cards rarely set `flavorText`; the descriptive subtitle
        // (e.g. "A thunderous clash of Ordinary Magic") lives in
        // `typeText`. Surface it so the flavor search can hit both fields.
        type_text: variant.typeText || '',
        image_url: buildSorceryImageUrl(variant.slug, imageBaseUrl, imageCacheBust),
        image_rotation_degrees: 0,
        tcgplayer_product_id: '',
        tcgplayer_url: '',
      });
    }
  }

  // Foils have their own distinct art (foil treatment + full-art back),
  // uploaded to Blob at the foil slug. Each printing keeps the image_url
  // built from its own variant slug above. No standard-art override.

  // Synthetic foil printings. The four Gothic precon avatars ship the
  // alt illustration in Standard only, but the progression precons grant
  // their avatar as a foil (see resolveStarterDeck), so the alt art needs
  // a foil printing to be grantable and to get its own deck-builder tile.
  // The `-pd-f` art in R2 is the `-pd-s` art re-uploaded under the foil
  // slug; the shine is the shader's, sampling the baked sibling maps.
  for (const printing of [...printings]) {
    const foilSlug = SYNTHETIC_FOIL_SLUGS[printing.unique_id];
    if (!foilSlug) continue;
    printings.push({
      ...printing,
      unique_id: foilSlug,
      set_printing_unique_id: foilSlug,
      id: foilSlug,
      edition: 'F',
      foiling: 'F',
      image_url: buildSorceryImageUrl(foilSlug, imageBaseUrl, imageCacheBust),
    });
  }

  const cardId = `sorcery-${slug}`;
  const backFaceDef = CARD_BACK_FACES[cardId] || null;

  // Front-face rules text. Some upstream entries (notably Druid) put
  // the *post-flip* "UPDATED" wording in guardian.rulesText, which
  // would otherwise mismatch the front-face image we render by
  // default. When a back-face override exists, prefer its explicit
  // front-face text so the grid/picker/inspector all line up.
  const rawFrontText = backFaceDef?.front?.rulesText || guardian.rulesText || '';

  // Build back-face printings keyed off the front-face slug whitelist.
  // Each back-face printing inherits the front printing's edition/foil
  // metadata and points at the CDN slug suffixed by `back.slugSuffix`.
  // Promotional reprints that already carry the post-flip wording are
  // not in the whitelist. They aren't flippable.
  let backFace = null;
  if (backFaceDef?.back) {
    const back = backFaceDef.back;
    const backFacePrintings = printings
      .filter((p) => back.variants?.includes(p.unique_id))
      .map((p) => {
        const backSlug = p.unique_id + back.slugSuffix;
        return {
          ...p,
          unique_id: backSlug,
          set_printing_unique_id: backSlug,
          id: backSlug,
          image_url: buildSorceryImageUrl(backSlug, imageBaseUrl, imageCacheBust),
        };
      });
    backFace = {
      name: back.name || raw.name,
      type: back.type || type,
      rulesText: back.rulesText || '',
      functional_text: back.rulesText || '',
      functional_text_plain: (back.rulesText || '').replace(/\r\n/g, '\n'),
      // Keep the structured "Rarity Type Subtypes" header on the back
      // so the inspector chip reads the same on both faces; the
      // per-printing flavor line ("force of nature!") stays in
      // back.typeText for surfaces that want the flavor copy.
      type_text: [guardian.rarity, back.type || type, ...subTypes].filter(Boolean).join(' '),
      type_text_flavor: back.typeText || '',
      flavor_text: back.flavorText || '',
      power: statToString(back.attack),
      defense: statToString(back.defence),
      health: statToString(back.life),
      thresholds: back.thresholds || null,
      printings: backFacePrintings,
    };
  }

  return {
    unique_id: cardId,
    name: raw.name,
    pitch: '',
    cost: statToString(guardian.cost),
    power: statToString(guardian.attack),
    defense: statToString(guardian.defence),
    health: statToString(guardian.life),
    intelligence: '',
    arcane: '',
    types: [type],
    traits: subTypes,
    card_keywords: [],
    abilities_and_effects: [],
    functional_text: rawFrontText,
    functional_text_plain: rawFrontText.replace(/\r\n/g, '\n'),
    type_text: [guardian.rarity, type, ...subTypes].filter(Boolean).join(' '),
    played_horizontally: type === 'Site',
    blitz_legal: false,
    cc_legal: false,
    printings,
    backFace,
    // Fields used by deckMetrics and archive filters
    elements: elements.map((name) => ({ id: name.toLowerCase(), name })),
    type: type,
    rarity: guardian.rarity || '',
    waterThreshold: guardian.thresholds?.water || 0,
    earthThreshold: guardian.thresholds?.earth || 0,
    fireThreshold: guardian.thresholds?.fire || 0,
    airThreshold: guardian.thresholds?.air || 0,
    // Sorcery-specific metadata
    _sorcery: true,
    _sorceryCategory: getSorceryCategory(type),
    // Auto-applied effect entries; empty unless the tagger matched the card.
    // Sidecar is keyed by the catalog slug (stable id), with the display
    // name as a legacy fallback for any stale cached copy.
    automation: desugarAutomationEntries(automationMap?.[cardId] || automationMap?.[raw.name] || []),
    // Printed keywords from the rules text. Used by status filters
    // in scope resolution and the Spellcaster-eligibility check.
    printedKeywords: extractPrintedKeywords(rawFrontText),
    // v1.52. Numeric bonus from "Movement +N" lines, used by the
    // multi-step BFS reach. 0 for units without the keyword.
    movementBonus: extractMovementBonus(rawFrontText),
    // v1.56. Numeric N from "Ranged N" lines. 0 for units without
    // the keyword. Combat enumerates a wider target set when > 0
    // and skips defender strike-back for projectile attacks.
    rangedValue: extractRangedValue(rawFrontText),
  };
}

export function normalizeSorceryCards(rawCards, imageBaseUrl = '', automationMap = null, imageCacheBust = FOIL_IMAGE_CACHE_BUST) {
  return (Array.isArray(rawCards) ? rawCards : []).map((card) => normalizeSorceryCard(card, imageBaseUrl, automationMap, imageCacheBust));
}

// Walks the raw Sorcery catalog and returns every image slug that must
// exist on disk for the client to never hit Vercel Blob at runtime.
//
// Includes:
//   - Every Standard and Foil variant slug per printing (foils have
//     their own distinct art). Rainbow is skipped (no upstream asset).
//   - Back-face slugs (variant.slug + slugSuffix) for the small set of
//     two-sided cards declared in CARD_BACK_FACES.
//
// Returns a deduped array. Stable across calls for a given catalog so
// the gate's "is every slug on disk?" check is deterministic.
export function collectAllExpectedSlugs(rawCards) {
  const slugs = new Set();
  for (const card of (Array.isArray(rawCards) ? rawCards : [])) {
    const cardKey = (card.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    const backDef = CARD_BACK_FACES[`sorcery-${cardKey}`]?.back || null;
    let hasStandard = false;
    const nonRainbowSlugs = [];
    for (const set of (card.sets || [])) {
      for (const variant of (set.variants || [])) {
        if (!variant.slug) continue;
        if (variant.finish === 'Standard') { slugs.add(variant.slug); hasStandard = true; }
        // Foils have their own art now. Prefetch the foil slug, its baked foil
        // map (R/G normal gradient, B mean-luma), and the monocular depth map
        // (~6KB) that drives the premium shader's relief + holo float.
        if (variant.finish === 'Foil') {
          slugs.add(variant.slug);
          slugs.add(variant.slug + '.foilmap4');
          slugs.add(variant.slug + '.depthmap');
        }
        if (variant.finish !== 'Rainbow') nonRainbowSlugs.push(variant.slug);
        if (backDef && backDef.variants?.includes(variant.slug)) {
          slugs.add(variant.slug + backDef.slugSuffix);
        }
      }
    }
    // Foil-only catalog entries (e.g. Winter River) carry no Standard
    // variant. The plain art lives at the foil slug and the foil look is
    // a client-side filter, so normalizeSorceryCard leaves the printing
    // pointed at that slug and the renderer requests it directly. Mirror
    // that here so the gate prefetches it; otherwise it 404s and spins.
    if (!hasStandard) {
      for (const slug of nonRainbowSlugs) slugs.add(slug);
    }
  }
  // Synthetic foils (the Gothic precon avatars). Same three assets as a real
  // foil: art copied from the standard printing, plus its baked foil + depth
  // maps. See syntheticFoils.js.
  for (const [standard, foil] of Object.entries(SYNTHETIC_FOIL_SLUGS)) {
    if (!slugs.has(standard)) continue;
    slugs.add(foil);
    slugs.add(foil + '.foilmap4');
    slugs.add(foil + '.depthmap');
  }
  return [...slugs];
}
